<script lang="ts">
type CallbackFn = () => void;
type ObserveFn = (element: Element, callback: CallbackFn) => () => void;

function useObserver(): { observe: ObserveFn } | undefined {
  const app = useApp();
  if (app._observer) {
    return app._observer;
  }

  let observer: IntersectionObserver | null = null;

  const callbacks = new Map<Element, CallbackFn>();

  const observe: ObserveFn = (element, callback) => {
    if (!observer) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const callback = callbacks.get(entry.target);
          const isVisible = entry.isIntersecting || entry.intersectionRatio > 0;
          if (isVisible && callback) {
            callback();
          }
        }
      });
    }
    callbacks.set(element, callback);
    observer.observe(element);

    return () => {
      callbacks.delete(element);
      observer!.unobserve(element);
      if (callbacks.size === 0) {
        observer!.disconnect();
        observer = null;
      }
    };
  };

  const _observer = (app._observer = {
    observe,
  });

  return _observer;
}
</script>

<script setup lang="ts">
import { hasProtocol } from 'ufo';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useApp } from '@/main';
import { preloadRouteComponents } from '@/plugins/01.router';

import type { RouteLocationRaw } from 'vue-router';

interface BasicLinkProps {
  to?: RouteLocationRaw;
  href?: RouteLocationRaw;
  target?: LiteralUnion<'_blank' | '_parent' | '_self' | '_top'>;
  external?: boolean;
}

const props = withDefaults(defineProps<BasicLinkProps>(), {
  to: undefined,
  href: undefined,
  target: undefined,
});

const router = useRouter();
const observer = useObserver();

const el = ref<HTMLElement>();

const _to = computed<string | RouteLocationRaw>(() => {
  return props.to || props.href || ''; // Defaults to empty string (won't render any `href` attribute)
});

// Resolves `to` value if it's a route location object
// converts `'''` to `null` to prevent the attribute from being added as empty (`href=""`)
const _href = computed(() => {
  return typeof _to.value === 'object'
    ? router.resolve(_to.value)?.href ?? null
    : _to.value || '';
});

// Resolving link type
const isExternal = computed(() => {
  // External prop is explictly set
  if (props.external) {
    return true;
  }

  // When `target` prop is set, link is external
  if (props.target && props.target !== '_self') {
    return true;
  }

  // When `to` is a route object then it's an internal link
  if (typeof _to.value === 'object') {
    return false;
  }

  return _to.value === '' || hasProtocol(_to.value, { acceptRelative: true });
});

const resolveRef = (ref: any) => {
  el.value = ref?.$el;
};

let idleId: number;
let unobserve: (() => void) | null = null;

onMounted(() => {
  requestIdleCallback(() => {
    idleId = requestIdleCallback(() => {
      if (!el.value) return;
      unobserve = observer!.observe(el.value, () => {
        unobserve?.();
        unobserve = null;

        if (isExternal.value) return;
        preloadRouteComponents(_to.value, router);
      });
    });
  });
});

onBeforeUnmount(() => {
  if (idleId) {
    cancelIdleCallback(idleId);
  }
  unobserve?.();
  unobserve = null;
});
</script>

<template>
  <template v-if="!isExternal">
    <RouterLink
      :ref="resolveRef"
      :to="_to"
    >
      <slot name="default" />
    </RouterLink>
  </template>
  <template v-else>
    <a
      ref="el"
      :href="_href"
      :target="target"
      rel="noopener noreferrer"
    >
      <slot name="default" />
    </a>
  </template>
</template>
@/plugins/01.router
